import { type ReactElement, type ReactNode, useEffect, useState } from "react"
import { EndUserInstanceLocationType } from "../../types/EndUserInstanceLocationType"
import { useSelector } from "react-redux";
import { RootState } from "src/app/store";
import Modal from "src/components/Modal";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import Button from "src/components/Button";
import Select from "react-select";
import { optionType } from "../../types/optionType";
import Saving from "src/components/Saving";

export interface EndUserInstanceLocationModalProps {
        title: string,
        isOpen: boolean,
        selectedEndUserInstanceLocation: EndUserInstanceLocationType | undefined,
        onClose: () => void,
        onSubmit: (value: EndUserInstanceLocationType) => void,
        endUserInstanceID: string,
        children?: ReactNode,
}

export default function EndUserInstanceLocationModal(props: EndUserInstanceLocationModalProps): ReactElement {
        const { control, register, handleSubmit, reset, clearErrors, setError, formState: { errors, isSubmitting } } = useForm<EndUserInstanceLocationType>({
                defaultValues: props.selectedEndUserInstanceLocation
        });
        // const labelStyle = "original";
        // const timezones = {
        //         ...allTimezones,
        //         "America/Chicago": "Chicago",
        // }
        //        const { options, parseTimezone } = useTimezoneSelect({ labelStyle, timezones });
        const { authUser } = useSelector((state: RootState) => state.auth);
        const { endUserInstanceLoading, endUserInstance } = useSelector((state: RootState) => state.endUserInstance);
        const { endUserInstanceLocation, endUserInstanceLocationError, endUserInstanceLocationLoading } = useSelector((state: RootState) => state.endUserInstanceLocation);
        const { endUserInstances } = useSelector((state: RootState) => state.endUserInstances);


        const onSubmit: SubmitHandler<EndUserInstanceLocationType> = (data) => {
                props.onSubmit(data);
        }

        useEffect(() => {
                let error = "";
                if (props.title === "Add EndUserInstance Location") {
                        clearErrors("root");
                }
                reset(props.selectedEndUserInstanceLocation);
                if (endUserInstanceLocationError !== null) {
                        error = endUserInstanceLocationError;
                }
                setError("root", {
                        type: "manual",
                        message: error,
                });
        }, [endUserInstanceLocationError])

        const authorized = authUser?.UserType === "admin" ? false : true;

        const options: optionType[] = [
                { value: '-12:00', label: '(UTC - 12: 00) International Date Line West' },
                { value: '-11:00', label: '(UTC - 11: 00) Midway Island, Samoa ' },
                { value: '-10:00', label: '(UTC - 10: 00) Hawaii ' },
                { value: '-09:30', label: '(UTC - 09: 30) Marquesas Islands ' },
                { value: '-09:00', label: '(UTC - 09: 00) Alaska ' },
                { value: '-08:00', label: '(UTC - 08: 00) Pacific Time(US & Canada) ' },
                { value: '-07:00', label: '(UTC - 07: 00) Mountain Time(US & Canada) ' },
                { value: '-06:00', label: '(UTC - 06: 00) Central Time(US & Canada), Chicago' },
                { value: '-05:00', label: '(UTC - 05: 00) Eastern Time(US & Canada), Bogota, Lima ' },
                { value: '-04:00', label: '(UTC - 04: 00) Atlantic Time(Canada), Caracas, La Paz ' },
                { value: '-03:30', label: '(UTC - 03: 30) Newfoundland ' },
                { value: '-03:00', label: '(UTC - 03: 00) Brazil, Buenos Aires, Georgetown ' },
                { value: '-02:00', label: '(UTC - 02: 00) Mid - Atlantic ' },
                { value: '-01:00', label: '(UTC - 01: 00) Azores, Cape Verde Islands ' },
                { value: '+00:00', label: '(UTC + 00: 00) Western Europe Time, London, Lisbon, Casablanca ' },
                { value: '+01:00', label: '(UTC + 01: 00) Brussels, Copenhagen, Madrid, Paris ' },
                { value: '+02:00', label: '(UTC + 02: 00) Kaliningrad, South Africa ' },
                { value: '+03:00', label: '(UTC + 03: 00) Baghdad, Riyadh, Moscow, St.Petersburg ' },
                { value: '+03:30', label: '(UTC + 03: 30) Tehran ' },
                { value: '+04:00', label: '(UTC + 04: 00) Abu Dhabi, Muscat, Baku, Tbilisi ' },
                { value: '+04:30', label: '(UTC + 04: 30) Kabul ' },
                { value: '+05:00', label: '(UTC + 05: 00) Ekaterinburg, Islamabad, Karachi, Tashkent ' },
                { value: '+05:30', label: '(UTC + 05: 30) Bombay, Calcutta, Madras, New Delhi ' },
                { value: '+05:45', label: '(UTC + 05: 45) Kathmandu ' },
                { value: '+06:00', label: '(UTC + 06: 00) Almaty, Dhaka, Colombo ' },
                { value: '+06:30', label: '(UTC + 06: 30) Yangon, Cocos Islands ' },
                { value: '+07:00', label: '(UTC + 07: 00) Bangkok, Hanoi, Jakarta ' },
                { value: '+08:00', label: '(UTC + 08: 00) Beijing, Perth, Singapore, Hong Kong ' },
                { value: '+08:45', label: '(UTC + 08: 45) Eucla ' },
                { value: '+09:00', label: '(UTC + 09: 00) Tokyo, Seoul, Osaka, Sapporo, Yakutsk ' },
                { value: '+09:30', label: '(UTC + 09: 30) Adelaide, Darwin ' },
                { value: '+10:00', label: '(UTC + 10: 00) Eastern Australia, Guam, Vladivostok ' },
                { value: '+10:30', label: '(UTC + 10: 30) Lord Howe Island ' },
                { value: '+11:00', label: '(UTC + 11: 00) Magadan, Solomon Islands, New Caledonia ' },
                { value: '+12:00', label: '(UTC + 12: 00) Auckland, Wellington, Fiji, Kamchatka ' },
                { value: '+12:45', label: '(UTC + 12: 45) Chatham Islands ' },
                { value: '+13:00', label: '(UTC + 13: 00) Nukualofa' },
                { value: '+14:00', label: '(UTC + 14: 00) Kiritimati' },
        ];

        return <>
                <Modal isOpen={props.isOpen} modalSize="lg" onClose={props.onClose}>
                        <h2 className="text-lg font-bold text-center">
                                {props.title}
                        </h2>
                        <form className="flex flex-col gap-5" onSubmit={handleSubmit(onSubmit)}>
                                <div className="mb-4 flex flex-col items-center">
                                        <label className="block text-sm">EndUser Instance Name</label>
                                        <input className="w-full px-3 py-2 border text-center rounded-lg" value={endUserInstances.find(i => i.Id === Number(props.endUserInstanceID))?.Name} disabled={true} />
                                </div>
                                <div className="flex gap-4">
                                        <div className="flex flex-col flex-grow-0 basis-1/2">
                                                {/* <div className="mb-4"> */}
                                                {/*         <label className="block text-sm">EndUser Instance Location ID</label> */}
                                                {/*         <input className="w-full px-3 py-2 border rounded-lg" type="text" {...register("EndUserInstanceLocationId", { required: "Id is required" })} /> */}
                                                {/* </div> */}
                                                {/* {errors.Id && ( */}
                                                {/*         <div className="text-red-500">{errors.Id.message}</div> */}
                                                {/* )} */}
                                                <div className="mb-4">
                                                        <label className="block text-sm">Name *</label>
                                                        <input className="w-full px-3 py-2 border rounded-lg" type="text" {...register("LocationName", { required: "Location Name is required" })} />
                                                </div>
                                                {errors.LocationName && (
                                                        <div className="text-red-500">{errors.LocationName.message}</div>
                                                )}
                                                <div className="mb-4">
                                                        <label className="block text-sm">Location Number *</label>
                                                        <input className="w-full px-3 py-2 border rounded-lg" type="text" {...register("LocationNumber", { required: "Location Number is required", min: { value: 1, message: "Location Number must be greater than 0" } })} />
                                                </div>
                                                {errors.LocationName && (
                                                        <div className="text-red-500">{errors.LocationName.message}</div>
                                                )}
                                                <div className="mb-4">
                                                        <label className="block text-sm">Timezone Offset *</label>
                                                        <div className="w-80">
                                                                <Controller
                                                                        name="TimezoneOffset"
                                                                        control={control}
                                                                        rules={{ required: 'Timezone is required' }}
                                                                        render={({ field }) => (
                                                                                <Select
                                                                                        {...field}
                                                                                        options={options}
                                                                                />
                                                                        )}
                                                                />
                                                        </div>
                                                </div>
                                                {errors.TimezoneOffset && (
                                                        <div className="text-red-500">{errors.TimezoneOffset.message}</div>
                                                )}
                                                <div className="mb-4">
                                                        <label className="block text-sm">DST</label>
                                                        <input className="px-3 py-2 border rounded-lg" type="checkbox" {...register("DST")} />
                                                </div>
                                                {errors.DST && (
                                                        <div className="text-red-500">{errors.DST.message}</div>
                                                )}
                                        </div>
                                        <div className="flex flex-col flex-grow-0 basis-1/2">
                                                <div className="mb-4">
                                                        <label className="block text-sm">Number of Doors *</label>
                                                        <input className="w-full px-3 py-2 border rounded-lg" disabled={authorized} type="number" {...register("NumberOfDoors", { required: "Number Of Doors is required" })} />
                                                </div>
                                                {errors.NumberOfDoors && (
                                                        <div className="text-red-500">{errors.NumberOfDoors.message}</div>
                                                )}
                                                <div className="mb-4">
                                                        <label className="block text-sm">Sales Order Number *</label>
                                                        <input className="w-full px-3 py-2 border rounded-lg" disabled={authorized} type="number" {...register("SalesOrderNumber", { required: "Sales Order Number is required" })} />
                                                </div>
                                                {errors.SalesOrderNumber && (
                                                        <div className="text-red-500">{errors.SalesOrderNumber.message}</div>
                                                )}
                                                <div className="mb-4 flex items-center">
                                                        <label className="block flex-auto text-sm">MAC Address *</label>
                                                        <input type="text" className="w-full flex-[1_0_0] px-3 py-2 border rounded-lg" disabled={authorized} {...register("MACAddress", { required: "MAC Address is required", maxLength: { value: 6, message: "Length of MAC should be atleast 6" }, min: { value: 6, message: "Length of MAC should be atleast 6" } })} />
                                                </div>
                                                {errors.MACAddress && (
                                                        <div className="text-red-500">{errors.MACAddress.message}</div>
                                                )}
                                                <div className="mb-4">
                                                        <label className="block text-sm">Connection Status</label>
                                                        <input className="px-3 py-2 border rounded-lg" type="checkbox" {...register("ConnectionStatus")} disabled={true} />
                                                </div>
                                                {errors.ConnectionStatus && (
                                                        <div className="text-red-500">{errors.ConnectionStatus.message}</div>
                                                )}
                                        </div>
                                </div>
                                <div className="flex gap-16 justify-around">
                                        <Button className="" size="lg" type="reset" buttonStyle="delete">Reset</Button>
                                        <Button className="" size="lg" type="submit" buttonStyle="primary">
                                                {endUserInstanceLocationLoading ? <Saving title="Saving" color="black" /> : "Save"}
                                        </Button>
                                </div>
                        </form>
                        {errors.root && (
                                <div className="text-red-500 text-center">{errors.root.message}</div>
                        )}
                </Modal>
        </>
}
